import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { convertVolume } from "../helpers/convertVolume";
import { normalizeVolume } from "../helpers/normalizeVolume";

const MONTH_LENGTH = 30.436875;

const useStyles = makeStyles({
  root: {
    // background: 'white',
    border: 0,
    color: 'text.primary',
    height: 'auto',
    width: '100%',
    // padding: '10px',
    overflow: 'hidden',
    // margin: 10
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  },
  table: {
    overflow: 'auto',
    minHeight: '100px',
    height: 700,
    marginTop: 0,
    marginBottom: 20,
  },
});

export const ForecastDataTable = ({ well, fluid, units, normalization, typeCurve, typeCurveCurrent }) => {
  try {
    // STYLES
    const classes = useStyles();
    // const useWaterflood = well?.params?.oil?.useWaterflood;

    let columns = [
      {
        field: 'monthsOnline',
        headerName: 'Months',
        width: 75,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: true,
      },
      {
        field: 'date',
        headerName: 'Date',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: true,
        type: 'string'
      },
      {
        field: 'volumeP90',
        headerName: `Vol. P90 (${getUnits(fluid, units)})`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'volumeP50',
        headerName: `Vol. P50 (${getUnits(fluid, units)})`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'volumeP10',
        headerName: `Vol. P10 (${getUnits(fluid, units)})`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'rateP90',
        headerName: `Rate P90 (${getUnits(fluid, units)}/d)`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'rateP50',
        headerName: `Rate P50 (${getUnits(fluid, units)}/d)`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'rateP10',
        headerName: `Rate P10 (${getUnits(fluid, units)}/d)`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'cumVolumeP90',
        headerName: `Cum. Vol. P90 (${getUnits(fluid, units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'cumVolumeP50',
        headerName: `Cum. Vol. P50 (${getUnits(fluid, units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'cumVolumeP10',
        headerName: `Cum. Vol. P10 (${getUnits(fluid, units)})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
      },
    ];

    if (typeCurveCurrent) { // filter out the date column for type curves
      columns = columns.filter(c => c.field !== 'date');
    };

    let rows = [];

    if (!well?.copy) {

      const lastProductionMonth = well?.production?.byFluid?.[fluid]?.slice(-1)?.[0]?.monthsOnline;

      if (well?.forecast) {
        if (normalization === 'off' || (normalization !== 'off' && typeCurve?.details && well?.completions)) {
          well?.forecast?.byFluid[fluid]?.forEach((value, index) => {
            const date = new Date(value?.date)?.toDateString()?.split(' ');
            const monthsOnline = value?.monthsOnline;
            let prodVolume = null;
            let cumVolume = null;
            if (monthsOnline <= lastProductionMonth) {
              prodVolume = Number((normalizeVolume(convertVolume(well?.production?.byFluid?.[fluid]?.find(p => p.monthsOnline === monthsOnline)?.volume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2));
              cumVolume = Number((normalizeVolume(convertVolume(well?.production?.byFluid?.[fluid]?.find(p => p.monthsOnline === monthsOnline)?.cumVolume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2));
            };
            rows.push({
              id: 'fluid-' + index,
              monthsOnline: (value?.monthsOnline || 0),
              date: `${date[1]}-${date[3]}`,
              volumeP10: (!typeCurveCurrent && prodVolume) ? prodVolume : Number((normalizeVolume(convertVolume(value?.volume?.p10, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              volumeP50: (!typeCurveCurrent && prodVolume) ? prodVolume : Number((normalizeVolume(convertVolume(value?.volume?.p50, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              volumeP90: (!typeCurveCurrent && prodVolume) ? prodVolume : Number((normalizeVolume(convertVolume(value?.volume?.p90, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              rateP10: (!typeCurveCurrent && prodVolume) ? Number((prodVolume / MONTH_LENGTH)?.toFixed(2)) : Number(((normalizeVolume(convertVolume(value?.volume?.p10, units, fluid), normalization, typeCurve?.details, well?.completions) || 0) / MONTH_LENGTH)?.toFixed(2)),
              rateP50: (!typeCurveCurrent && prodVolume) ? Number((prodVolume / MONTH_LENGTH)?.toFixed(2)) : Number(((normalizeVolume(convertVolume(value?.volume?.p50, units, fluid), normalization, typeCurve?.details, well?.completions) || 0) / MONTH_LENGTH)?.toFixed(2)),
              rateP90: (!typeCurveCurrent && prodVolume) ? Number((prodVolume / MONTH_LENGTH)?.toFixed(2)) : Number(((normalizeVolume(convertVolume(value?.volume?.p90, units, fluid), normalization, typeCurve?.details, well?.completions) || 0) / MONTH_LENGTH)?.toFixed(2)),
              cumVolumeP10: (!typeCurveCurrent && cumVolume) ? cumVolume : Number((normalizeVolume(convertVolume(value?.cumVolume?.p10, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              cumVolumeP50: (!typeCurveCurrent && cumVolume) ? cumVolume :  Number((normalizeVolume(convertVolume(value?.cumVolume?.p50, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              cumVolumeP90: (!typeCurveCurrent && cumVolume) ? cumVolume : Number((normalizeVolume(convertVolume(value?.cumVolume?.p90, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2))
            });
          });
        };
      };
    } else { // new drill local wells
      if (well?.production) {
        if (normalization === 'off' || (normalization !== 'off' && typeCurve?.details && well?.completions)) {
          well?.production?.byFluid[fluid]?.forEach((value, index) => {
            const date = new Date(value?.date)?.toDateString()?.split(' ');

            rows.push({
              id: 'fluid-' + index,
              monthsOnline: (value?.monthsOnline || 0),
              date: `${date[1]}-${date[3]}`,
              volumeP10: Number((normalizeVolume(convertVolume(value?.volume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              volumeP50: Number((normalizeVolume(convertVolume(value?.volume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              volumeP90: Number((normalizeVolume(convertVolume(value?.volume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              rateP10: Number(((normalizeVolume(convertVolume(value?.volume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0) / MONTH_LENGTH)?.toFixed(2)),
              rateP50: Number(((normalizeVolume(convertVolume(value?.volume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0) / MONTH_LENGTH)?.toFixed(2)),
              rateP90: Number(((normalizeVolume(convertVolume(value?.volume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0) / MONTH_LENGTH)?.toFixed(2)),
              cumVolumeP10: Number((normalizeVolume(convertVolume(value?.cumVolume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              cumVolumeP50: Number((normalizeVolume(convertVolume(value?.cumVolume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2)),
              cumVolumeP90: Number((normalizeVolume(convertVolume(value?.cumVolume, units, fluid), normalization, typeCurve?.details, well?.completions) || 0)?.toFixed(2))
            });
          });
        };
      };
    };

    return (
      <div className={classes.root}>
        {well && <DataGrid
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[10,25,50,100]}
          disableSelectionOnClick
          className={classes.table}
          components={{
            Toolbar: GridToolbar,
          }}
          sx={{fontSize: 12}}
          density="compact"
        />}
      </div>
    );
  } catch (error) {
    return <></>
  };
};

function getUnits(fluid, units) {
  switch(fluid) {
    case 'oil':
      return units === 'metric' ? 'm3' : 'stb';
    case 'gas':
      return units === 'metric' ? 'E3m3' : 'mcf';
    case 'condensate':
      return units === 'metric' ? 'm3' : 'stb';
    case 'water':
      return units === 'metric' ? 'm3' : 'stb';
    default:
      return '';
  };
};